import React from 'react';
import {
  OFFERED_PACKAGE_TYPES,
  HOLDER_OPTIONS,
  MAX_PARTY_SIZE,
  bundleVisitNote,
  buildCustomerSentence,
  validateCompositionChoice,
  typeById,
} from '../../utils/packageCompositionCopy';
import './PackageTypeStep.css';

/**
 * The palette, one entry per type. Kept HERE rather than in the copy module: the copy module is what a test
 * asserts the WORDS against, and a colour is not a word. Each card sets these as CSS custom properties, so
 * one set of stylesheet rules covers all four.
 */
const TYPE_STYLES = {
  single: { icon: '\uD83C\uDFAB', accent: '#2563eb', accentText: '#1d4ed8', tint: '#eff6ff', soft: '#bfdbfe', ring: 'rgba(37, 99, 235, 0.35)' },
  family: { icon: '\uD83D\uDC6A', accent: '#7c3aed', accentText: '#6d28d9', tint: '#f5f3ff', soft: '#ddd6fe', ring: 'rgba(124, 58, 237, 0.35)' },
  bundle: { icon: '\uD83E\uDDE9', accent: '#d97706', accentText: '#b45309', tint: '#fffbeb', soft: '#fde68a', ring: 'rgba(217, 119, 6, 0.35)' },
  couple: { icon: '\uD83D\uDC9E', accent: '#e11d48', accentText: '#be123c', tint: '#fff1f2', soft: '#fecdd3', ring: 'rgba(225, 29, 72, 0.35)' },
};

const FALLBACK_STYLE = TYPE_STYLES.single;

/**
 * Phase 22A items 5 and 7 — the creation form asks the TYPE first, in plain language.
 *
 * Composition plan §3.4. Three things this component is responsible for, and nothing else:
 *
 * 1. **The type first.** Four plain-language cards, no model vocabulary ("quota", "per_person") anywhere on
 *    screen. The type then decides WHICH questions are asked, so a shop is never asked about a "covered
 *    people" figure on a package where each person takes their own session.
 * 2. **The customer's sentence, live.** §3.4: *"a type the builder cannot explain in one sentence is a type
 *    that will be mis-sold"*. The sentence is shown while the shop is still choosing, not on a screen added
 *    afterwards — and a type with no sentence is refused rather than saved blank.
 * 3. **What a bundle session IS.** "Each visit is one session", and a customer taking only some of the
 *    services still spends it (Decision 9, revised 2026-10-06). The earlier "all services required" wording
 *    was reversed by the user and must not come back — the copy lives in `packageCompositionCopy.js`, and the
 *    test asserts the phrase is absent from both files.
 *
 * It renders ONLY the model questions. Sessions, validity, services and pricing stay in the parent form, so
 * this step adds a question to the top of the form rather than restructuring it.
 */
export default function PackageTypeStep({
  typeId,
  holderModel = 'single',
  totalSessions = '',
  coveredPeople = '',
  lines = [],
  catalog = [],
  onChange,
}) {
  const type = typeById(typeId);

  // The names come from the catalog, not from the form rows: a row's `serviceName` is only set on a saved
  // package, so a brand-new package would show the sentence with blanks in it.
  const namedLines = lines.map((line) => {
    const service = catalog.find((entry) => entry._id === line.serviceId);
    return { ...line, name: service?.name || '' };
  });

  const sentence = type
    ? buildCustomerSentence({ typeId, holderModel, lines: namedLines, totalSessions, coveredPeople })
    : null;

  // Shown only once the shop has chosen and started answering — an error message next to an empty form is
  // noise, and an error naming a service nobody has chosen yet is noise that reads like a bug.
  const hasStarted = Boolean(type) && (namedLines.some((line) => line.serviceId) || Number(totalSessions) > 0);
  const check = hasStarted
    ? validateCompositionChoice({ typeId, lines: namedLines, totalSessions, coveredPeople })
    : null;

  const patch = (fields) => onChange(fields);

  return (
    <div className="package-type-step" style={{ marginBottom: '26px' }}>
      <div className="pk-step-header">
        <span className="pk-step-badge">Step 1</span>
        <span className="pk-step-title">What kind of package is this? *</span>
      </div>
      <p className="pk-step-hint">
        This decides what your customer gets. You will only be asked the questions this kind needs.
      </p>

      <div className="pk-type-grid" role="group" aria-label="What kind of package is this?">
        {OFFERED_PACKAGE_TYPES.map((option) => {
          const active = option.id === typeId;
          const palette = TYPE_STYLES[option.id] || FALLBACK_STYLE;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => patch({ packageType: option.id, ...baseAnswersFor(option.id) })}
              aria-pressed={active}
              className="pk-type-card"
              style={{
                '--pk-accent': palette.accent,
                '--pk-accent-text': palette.accentText,
                '--pk-tint': palette.tint,
                '--pk-soft': palette.soft,
                '--pk-ring': palette.ring,
              }}
            >
              <span className="pk-type-icon" aria-hidden="true">
                {palette.icon}
              </span>
              <span className="pk-type-body">
                <span className="pk-type-name">{option.name}</span>
                <span className="pk-type-description">{option.description}</span>
                <span className="pk-type-example">Example: {option.example}</span>
              </span>
              {active && (
                <span className="pk-type-tick" aria-hidden="true">
                  {'✓'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {type && (
        <div style={{ marginTop: '18px', display: 'grid', gap: '16px' }}>
          {/* Question 1 of §3.4, and the only one no type already answers: who may use it. */}
          <div className="pk-question">
            <span className="pk-question-label">Who can use it?</span>
            <div className="pk-choice-grid">
              {HOLDER_OPTIONS.map((option) => {
                const active = option.id === holderModel;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => patch({ holderModel: option.id })}
                    aria-pressed={active}
                    className="pk-choice-card"
                  >
                    <div className="pk-choice-label">{option.label}</div>
                    <div className="pk-choice-hint">{option.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* The party size, only where one session is shared by the party. */}
          {type.asksCoveredPeople && (
            <div className="pk-question">
              <span className="pk-question-label">How many people does one visit cover? *</span>
              <input
                type="number"
                min="1"
                max={MAX_PARTY_SIZE}
                value={coveredPeople}
                onChange={(event) => patch({ coveredPeople: event.target.value })}
                placeholder="2"
                className="pk-number-input"
              />
              <span className="pk-help">
                Up to {MAX_PARTY_SIZE} people, the same limit a group booking has.
              </span>
            </div>
          )}

          {/* What a bundle session is — the requirement of item 5, in the plan's own words. */}
          {type.model.sessionContents === 'bundle' && (
            <div className="pk-note">
              <span className="pk-note-icon" aria-hidden="true">
                {'ℹ️'}
              </span>
              <span>{bundleVisitNote(totalSessions || 1)}</span>
            </div>
          )}

          {/* THE SENTENCE THE CUSTOMER IS SOLD ON (§3.4). */}
          <div className="pk-preview">
            <div className="pk-preview-label">
              <span aria-hidden="true">👁</span>
              What your customer will see
            </div>
            <div className="pk-preview-text">
              {sentence || 'This kind of package cannot be described to a customer yet.'}
            </div>
          </div>

          {check && !check.ok && (
            <div role="alert" className="pk-error">
              <span aria-hidden="true">⚠️</span>
              <span>{check.message}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The answers a type starts with.
 *
 * A TYPE CHANGE MUST NOT LEAVE THE PREVIOUS TYPE'S ANSWERS BEHIND. `coveredPeople` on a per-person package is
 * the state the server refuses outright (`composition_covered_people_not_applicable`), and a bundle's
 * quantities must be 1 for the same reason — so switching type resets exactly those, rather than trusting the
 * shop to clear a field that is about to disappear from the screen.
 */
function baseAnswersFor(typeId) {
  const type = typeById(typeId);
  if (!type) return {};
  if (type.model.consumptionModel === 'shared_per_visit') return { coveredPeople: '' };
  // A bundle is no longer forced to ONE visit (2026-10-09): the pricing engine multiplies a bundle line by its
  // visit count now, so the shop says how many visits it sells and the price follows. Forcing `totalSessions: 1`
  // here would silently re-impose the old cap in the form after it was lifted everywhere else.
  return { coveredPeople: '' };
}
